import { type ValidationRule } from '../../types';

/**
 * Rule: A live decorator step whose children are all suppressed is a blocking
 * error — it would execute as an empty container.
 *
 * Runs only on non-suppressed subjects (`ruleRunner` skips effectively-
 * suppressed steps), so no explicit self-suppression check is needed here.
 */
const allChildrenSuppressed: ValidationRule = {
  id: 'decorator.allChildrenSuppressed',
  appliesTo: 'step',
  run({ routine, stepId }) {
    if (!stepId) return [];

    const step = routine.derived?.stepById[stepId];
    if (!step) return [];

    // Only applies to decorator steps (steps with children)
    if (!step.children || step.children.length === 0) return [];

    // Check if ALL children are effectively suppressed
    const allSuppressed = step.children.every((childId) => {
      const child = routine.derived?.stepById[childId];

      return child?.effectivelySuppressed === true;
    });

    if (!allSuppressed) return [];

    return [
      {
        code: 'decorator.allChildrenSuppressed',
        message: 'All steps inside are suppressed.',
        severity: 'error' as const,
        stepId,
        sourceRule: 'decorator.allChildrenSuppressed',
      },
    ];
  },
};

/**
 * Rule: A live `ElseIf`/`Else` that has no live `If` immediately preceding
 * it (after cutting suppressed siblings) is a blocking error.
 *
 * Suppressed siblings of any kind are "cut" — skipped during the backward
 * walk. A live non-conditional sibling still breaks the chain, even if a
 * live `If` exists further back. Example:
 *   - `If → If(suppressed) → Else` — valid (suppressed If cut, Else pairs
 *     with live If)
 *   - `If → Wait → If(suppressed) → Else` — invalid (live Wait breaks chain)
 *
 * Runs only on non-suppressed subjects (`ruleRunner` skips effectively-
 * suppressed steps).
 */
const orphanedElseBlock: ValidationRule = {
  id: 'conditional.orphanedElseBlock',
  appliesTo: 'step',
  stepKinds: ['ElseIf', 'Else'],
  run({ routine, stepId }) {
    if (!stepId) return [];

    const step = routine.derived?.stepById[stepId];
    if (!step) return [];

    // Find the If step that starts this chain by looking at siblings
    const parents = routine.derived?.parentsById[stepId] ?? [];
    const immediateParentId = parents[0]?.id;

    // Find siblings: steps that share the same parent. `routine.steps` is the
    // DFS-flattened list of every step, so for the top-level case we filter to
    // only steps with an empty parent chain.
    let siblings: typeof routine.steps;

    if (immediateParentId) {
      const parent = routine.derived?.stepById[immediateParentId];

      siblings = parent?.children
        ? (parent.children
            .map((id) => routine.derived?.stepById[id])
            .filter(Boolean) as typeof routine.steps)
        : [];
    } else {
      siblings = routine.steps.filter(
        (s) => (routine.derived?.parentsById[s.id] ?? []).length === 0,
      );
    }

    // Walk backward, skipping suppressed siblings (cut from chain)
    const myIndex = siblings.findIndex((s) => s.id === stepId);
    if (myIndex < 0) return [];

    let foundLiveIf = false;

    for (let i = myIndex - 1; i >= 0; i -= 1) {
      const sibling = siblings[i];

      // Suppressed siblings are "cut" — skip regardless of kind
      if (sibling.effectivelySuppressed) continue;

      // Live If — valid pairing
      if (sibling.stepKind === 'If') {
        foundLiveIf = true;
        break;
      }

      // Live ElseIf — part of the chain, keep walking
      if (sibling.stepKind === 'ElseIf') continue;

      // Live non-conditional step — breaks the chain
      break;
    }

    if (foundLiveIf) return [];

    const label = step.stepKind === 'ElseIf' ? 'Else If' : 'Else';

    return [
      {
        code: 'conditional.orphanedElseBlock',
        message: `${label} must be part of a valid If block.`,
        severity: 'error' as const,
        stepId,
        sourceRule: 'conditional.orphanedElseBlock',
      },
    ];
  },
};

/**
 * Rule: a routine with at least one step where every top-level step is
 * effectively suppressed has nothing to execute — block play. Empty
 * routines (no steps at all) are out of scope for this rule.
 */
const allStepsSuppressed: ValidationRule = {
  id: 'routine.allStepsSuppressed',
  appliesTo: 'routine',
  run({ routine }) {
    if (routine.steps.length === 0) return [];

    const everySuppressed = routine.steps.every(
      (s) => s.effectivelySuppressed === true,
    );

    if (!everySuppressed) return [];

    return [
      {
        code: 'routine.allStepsSuppressed',
        message: 'Cannot run routine — all steps are suppressed.',
        severity: 'error' as const,
        sourceRule: 'routine.allStepsSuppressed',
      },
    ];
  },
};

export const SuppressedValidation = [
  allChildrenSuppressed,
  orphanedElseBlock,
  allStepsSuppressed,
];
